import React from 'react'
import classNames from 'classnames'
import { Icon } from '@/ui/atoms/Icon'
import Link from 'next/link'

export type TrendingLink = {
  label: string | Record<string, string>
  href: string
}

export type TrendingCardProps = {
  title: string | Record<string, string>
  icon?: string | null
  links: TrendingLink[]
  className?: string
  appearance?: 'filled' | 'outlined'
}

export const TrendingCard: React.FC<TrendingCardProps> = ({
  title,
  icon,
  links,
  appearance = 'filled',
  className: classNameFromProps,
}) => {
  // Helper to get string value from potentially localized field
  const getString = (value: string | Record<string, string> | undefined): string => {
    if (!value) return ''
    if (typeof value === 'string') return value
    // For localized objects, return the first available value
    return Object.values(value)[0] || ''
  }

  return (
    <div
      className={classNames(
        'flex flex-col gap-4 rounded-md border border-border-subtle bg-white transition-all duration-200 group p-4',
        classNameFromProps,
      )}
    >
      {/* Header with Icon and Title */}
      <div className="flex items-center gap-2">
        {icon && (
          <div className="flex-shrink-0 rounded-sm bg-bg-subtle p-1">
            <Icon name={icon} className="w-12 h-12" useGradient />
          </div>
        )}
        <h4 className="text-headline-XS-semibold text-text-base">{getString(title)}</h4>
      </div>

      {/* Links Column */}
      <div className="flex flex-col gap-4">
        {links.map((link, index) => (
          <Link
            key={index}
            href={link.href}
            className="flex items-center justify-between gap-3 group/link"
          >
            <span className="text-body-S-regular text-text-base canhover:group-hover/link:text-blue-40 transition-colors duration-200">
              {getString(link.label)}
            </span>
            <span className="rounded-sm bg-neutral-95 min-w-4 size-4 text-label-XS-semibold flex items-center justify-center flex-shrink-0">
              <span className="bg-soft-gradient-blue bg-clip-text text-transparent">
                {index + 1}
              </span>
            </span>
          </Link>
        ))}
      </div>
    </div>
  )
}
